// ═══════════════════════════════════════════════════════════════════════════
// ForgeTrack API Types & Hooks — Effort, Reports, AI Providers
// apps/web/src/lib/api/effort-reports.ts
// ═══════════════════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback } from 'react';
import { useApiClient } from '@forgetrack/api-client';
import type { ApiError } from './types';

// ─── Generic fetch ──────────────────────────────────────────────────────────

interface UseApiState<T> {
  data: T | null;
  loading: boolean;
  error: ApiError | null;
  refetch: () => Promise<void>;
}

function useApiFetch<T>(path: string, enabled = true): UseApiState<T> {
  const { createApi } = useApiClient();
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<ApiError | null>(null);

  const fetch = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const api = await createApi();
      const response = await api.get<T>(path);
      setData(response.data);
    } catch (err: any) {
      setError({ message: err.response?.data?.message || err.message || 'Request failed', status: err.response?.status || 500 });
    } finally {
      setLoading(false);
    }
  }, [createApi, path]);

  useEffect(() => { if (enabled) fetch(); }, [fetch, enabled]);
  return { data, loading, error, refetch: fetch };
}

// ─── Effort Types ───────────────────────────────────────────────────────────

export interface Benchmark {
  id: number;
  tenantId: number | null;
  workItemId: number | null;
  category: string;
  industryBenchmarkWeeks: number;
  teamSizeAssumed: string;
  expertiseRequiredYears: number;
  benchmarkSource: string | null;
  marketRateCost: number;
  comparableImplementations: string[];
  isGlobal: boolean;
  notes: string | null;
}

export interface CalculationResult {
  workItemId: number;
  baseWeeks: number;
  architecturalDepthMultiplier: number;
  integrationMultiplier: number;
  domainExpertiseMultiplier: number;
  infrastructureExpertiseMultiplier: number;
  securityExpertiseMultiplier: number;
  noveltyMultiplier: number;
  expertiseAcquisitionOverhead: number;
  coordinationOverhead: number;
  totalEquivalentWeeks: number;
  impliedTeamSize: number;
  marketRateCost: number;
}

export interface AIEstimateResult {
  workItemId: number;
  estimatedWeeks: number;
  confidence: number;
  comparableProjects: string[];
  rationale: string;
  aiModel: string;
  tokensUsed: number;
  cost: number;
}

export interface CreateBenchmarkRequest {
  category: string;
  industryBenchmarkWeeks: number;
  teamSizeAssumed?: string;
  expertiseRequiredYears?: number;
  marketRateCost?: number;
  comparableImplementations?: string[];
  notes?: string;
}

// ─── Report Types ───────────────────────────────────────────────────────────

export type ReportType =
  | 'RDCreditNarrative'
  | 'InvestorSummary'
  | 'EngineeringVelocity'
  | 'CPAExport'
  | 'VCDataRoom'
  | 'Custom';

export type ExportFormat = 'PDF' | 'XLSX' | 'CSV' | 'JSON' | 'HTML';

export interface Report {
  id: number;
  tenantId: number;
  projectId: number;
  reportType: ReportType;
  generatedDate: string;
  periodStart: string;
  periodEnd: string;
  locale: string;
  exportFormat: ExportFormat;
  status: 'Generating' | 'Ready' | 'Failed';
  contentPdfUrl: string | null;
  projectName?: string;
}

export interface GenerateReportRequest {
  projectId: number;
  reportType: ReportType;
  exportFormat: ExportFormat;
  periodStart?: string;
  periodEnd?: string;
  locale?: string;
}

export interface VelocityData {
  period: string;
  actualHours: number;
  equivalentWeeks: number;
  compressionRatio: number;
  sessionsCount: number;
  workItemsCompleted: number;
}

// ─── AI Provider Types ──────────────────────────────────────────────────────

export type ProviderType = 'Anthropic' | 'OpenAI' | 'xAI' | 'Google' | 'Ollama' | 'Custom';

export interface AIProvider {
  id: number;
  tenantId: number;
  name: string;
  providerType: ProviderType;
  apiEndpoint: string | null;
  isActive: boolean;
  hasApiKey: boolean;
  apiKey?: string;
  defaultModel: string;
  costPerInputToken: number;
  costPerOutputToken: number;
  totalTokensUsed: number;
  totalCostUSD: number;
}

export interface AIProviderUsage {
  month: string;
  taskType: string;
  callCount: number;
  tokensUsed: number;
  cost: number;
}

// ─── Effort Hooks ───────────────────────────────────────────────────────────

export function useBenchmarks() {
  return useApiFetch<Benchmark[]>('/api/v1/effort/benchmarks');
}

export function useEffortMutations() {
  const { createApi } = useApiClient();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const calculate = useCallback(async (workItemId: number): Promise<CalculationResult | null> => {
    try {
      setLoading(true); setError(null);
      const api = await createApi();
      const res = await api.post<CalculationResult>('/api/v1/effort/calculate', { workItemId });
      return res.data;
    } catch (err: any) {
      setError({ message: err.response?.data?.message || 'Calculation failed', status: err.response?.status || 500 });
      return null;
    } finally { setLoading(false); }
  }, [createApi]);

  const aiEstimate = useCallback(async (workItemId: number): Promise<AIEstimateResult | null> => {
    try {
      setLoading(true); setError(null);
      const api = await createApi();
      const res = await api.post<AIEstimateResult>('/api/v1/effort/ai-estimate', { workItemId });
      return res.data;
    } catch (err: any) {
      setError({ message: err.response?.data?.message || 'AI estimation failed', status: err.response?.status || 500 });
      return null;
    } finally { setLoading(false); }
  }, [createApi]);

  const override = useCallback(async (workItemId: number, equivalentWeeks: number): Promise<boolean> => {
    try {
      setLoading(true);
      const api = await createApi();
      await api.put('/api/v1/effort/override', { workItemId, equivalentWeeks });
      return true;
    } catch { return false; }
    finally { setLoading(false); }
  }, [createApi]);

  const recalcProject = useCallback(async (projectId: number): Promise<boolean> => {
    try {
      setLoading(true);
      const api = await createApi();
      await api.post(`/api/v1/effort/recalc/${projectId}`);
      return true;
    } catch { return false; }
    finally { setLoading(false); }
  }, [createApi]);

  const createBenchmark = useCallback(async (data: CreateBenchmarkRequest): Promise<Benchmark | null> => {
    try {
      setLoading(true);
      const api = await createApi();
      const res = await api.post<Benchmark>('/api/v1/effort/benchmarks', data);
      return res.data;
    } catch { return null; }
    finally { setLoading(false); }
  }, [createApi]);

  return { calculate, aiEstimate, override, recalcProject, createBenchmark, loading, error };
}

// ─── Report Hooks ───────────────────────────────────────────────────────────

export function useReports(projectId?: number) {
  const path = projectId ? `/api/v1/reports?projectId=${projectId}` : '/api/v1/reports';
  return useApiFetch<Report[]>(path);
}

export function useVelocityData(projectId: number) {
  return useApiFetch<VelocityData[]>(`/api/v1/reports/velocity?projectId=${projectId}`, projectId > 0);
}

export function useReportMutations() {
  const { createApi } = useApiClient();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const generate = useCallback(async (data: GenerateReportRequest): Promise<Report | null> => {
    try {
      setLoading(true); setError(null);
      const api = await createApi();
      const res = await api.post<Report>('/api/v1/reports/generate', data);
      return res.data;
    } catch (err: any) {
      setError({ message: err.response?.data?.message || 'Report generation failed', status: err.response?.status || 500 });
      return null;
    } finally { setLoading(false); }
  }, [createApi]);

  const downloadUrl = useCallback(async (reportId: number): Promise<string | null> => {
    try {
      const api = await createApi();
      const res = await api.get<{ url: string }>(`/api/v1/export/${reportId}`);
      return res.data.url;
    } catch { return null; }
  }, [createApi]);

  const remove = useCallback(async (id: number): Promise<boolean> => {
    try {
      const api = await createApi();
      await api.delete(`/api/v1/reports/${id}`);
      return true;
    } catch { return false; }
  }, [createApi]);

  return { generate, downloadUrl, remove, loading, error };
}

// ─── AI Provider Hooks ──────────────────────────────────────────────────────

export function useAIProviders() {
  return useApiFetch<AIProvider[]>('/api/v1/ai-providers');
}

export function useAIProviderUsage(providerId: number) {
  return useApiFetch<AIProviderUsage[]>(`/api/v1/ai-providers/${providerId}/usage`, providerId > 0);
}

export interface ModelInfo {
  modelId: string;
  displayName: string;
  inputCostPerMTok: number | null;
  outputCostPerMTok: number | null;
}

export function useModelList(providerType: string) {
  return useApiFetch<ModelInfo[]>(`/api/v1/ai-providers/models?provider=${encodeURIComponent(providerType)}`, !!providerType);
}

export function useAIProviderMutations() {
  const { createApi } = useApiClient();
  const [loading, setLoading] = useState(false);

  const create = useCallback(async (data: Partial<AIProvider>): Promise<AIProvider | null> => {
    try {
      setLoading(true);
      const api = await createApi();
      const res = await api.post<AIProvider>('/api/v1/ai-providers', data);
      return res.data;
    } catch { return null; }
    finally { setLoading(false); }
  }, [createApi]);

  const update = useCallback(async (id: number, data: Partial<AIProvider>): Promise<AIProvider | null> => {
    try {
      setLoading(true);
      const api = await createApi();
      const res = await api.put<AIProvider>(`/api/v1/ai-providers/${id}`, data);
      return res.data;
    } catch { return null; }
    finally { setLoading(false); }
  }, [createApi]);

  const remove = useCallback(async (id: number): Promise<boolean> => {
    try {
      setLoading(true);
      const api = await createApi();
      await api.delete(`/api/v1/ai-providers/${id}`);
      return true;
    } catch { return false; }
    finally { setLoading(false); }
  }, [createApi]);

  return { create, update, remove, loading };
}
